<template>
  <div v-bind:key="refreshKey">

    <p class="text-muted mb-50" v-if="canReorder">
      <i class="fa-solid fa-up-down mr-25"></i>{{ $t('psp.dragHint') }}
    </p>

    <div class="psp-list">
      <div class="psp-list__row psp-list__row--head">
        <span class="psp-list__cell psp-list__cell--grip"></span>
        <span class="psp-list__cell psp-list__cell--pos">#</span>
        <span class="psp-list__cell psp-list__cell--name">{{ $t('columns.name') }}</span>
        <span class="psp-list__cell psp-list__cell--active">{{ $t('columns.active') }}</span>
        <span class="psp-list__cell psp-list__cell--time">{{ $t('columns.creationTime') }}</span>
        <span class="psp-list__cell psp-list__cell--actions text-center">{{ $t('columns.actions') }}</span>
      </div>

      <draggable v-model="psps" handle=".psp-list__grip" :disabled="!canReorder" @end="onReorder">
        <div v-for="(psp, idx) in psps" :key="psp.id" class="psp-list__row">
          <span class="psp-list__cell psp-list__cell--grip">
            <i class="fa-solid fa-grip-vertical psp-list__grip" :class="{ 'psp-list__grip--off': !canReorder }"></i>
          </span>
          <span class="psp-list__cell psp-list__cell--pos text-muted">{{ idx + 1 }}</span>
          <span class="psp-list__cell psp-list__cell--name font-weight-bold">{{ psp.name }}</span>
          <span class="psp-list__cell psp-list__cell--active">
            <b-badge v-if="psp.active !== false" variant="light-success">{{ $t('operations.yes') }}</b-badge>
            <b-badge v-else variant="light-danger">{{ $t('operations.no') }}</b-badge>
          </span>
          <span class="psp-list__cell psp-list__cell--time">{{ psp.creationTime | momentDateTime }}</span>
          <span class="psp-list__cell psp-list__cell--actions text-center">
            <span class="btn-container" v-if="hasPermission(permissions.PSP_MANAGE)">
              <b-button size="sm" variant="success" v-b-tooltip v-bind:title="$t('operations.edit')"
                        @click.stop="onEdit(psp)"
              >
                <i class="fas fa-pencil-alt"></i>
              </b-button>
              <b-button size="sm" variant="danger" v-b-tooltip v-bind:title="$t('operations.delete')"
                        @click.stop="onDelete(psp)"
              >
                <i class="fas fa-trash"></i>
              </b-button>
            </span>
          </span>
        </div>
      </draggable>

      <div v-if="!psps.length" class="text-center text-muted py-2">
        {{ $t('psp.noData') }}
      </div>
    </div>

    <div class="mt-1 text-right text-muted">
      {{ psps.length }} {{ $t('columns.counterNames.psps') }}
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import draggable from 'vuedraggable';
import mixinTable from '@/components/mixin/mixinTable';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import mixinBase from '@/components/mixin/mixinBase';
import mixinPermissions from '@/components/mixin/mixinPermissions';

export default {
  name: 'PspTable',
  components: { draggable },
  mixins: [mixinBase, mixinTable, mixinNotifications, mixinPermissions],
  data: () => ({
    psps: [],
  }),
  props: {

    filters: {
      default: () => ({})
    },
    columns: {
      default: () => ([])
    },
    lazy: {
      default: () => false
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.refresh();
      }
    }
  },
  created() {
    this.setPagination(1, 0, 500);
    this.refresh();
  },
  computed: {
    // Dragging is only meaningful over the FULL list — a search shows a
    // subset, so reordering there would scramble the hidden rows' positions.
    canReorder() {
      return this.hasPermission(this.permissions.PSP_MANAGE) && !this.hasActiveFilter;
    },
    hasActiveFilter() {
      const f = this.filters || {};
      return Boolean(f.search || f.name || f.id);
    },
  },
  methods: {
    ...mapActions('psp', ['getAllPsps', 'deletePsp', 'reorderPsps']),
    onEdit(psp) {
      this.$emit('selected', psp.id);
    },
    // Drop → the visual order IS the dropdown order; persist index as sortOrder.
    async onReorder() {
      if (!this.canReorder) return;
      const ids = this.psps.map(p => p.id);
      try {
        await this.reorderPsps(ids);
        this.psps.forEach((p, i) => { p.sortOrder = i; });
        this.showSuccessNotification(this.$t('psp.reorderSaved'));
      } catch (err) {
        this.showErrorNotification(this.$t('globalMessages.updateDataError', { system: this.$t('menu.psps') }), this.getErrorDescription(err));
        this.refresh();
      }
    },
    onDelete(psp) {
      let $this = this;
      this.$bvModal.msgBoxConfirm($this.$t('operations.delete') + ' "' + psp.name + '"?', {
        title: $this.$t('globalMessages.pleaseConfirm'),
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: $this.$t('operations.yes'),
        cancelTitle: $this.$t('operations.no'),
        footerClass: 'p-2',
        centered: true,
      })
          .then(value => {
            if (!value) return;
            $this.deletePsp(psp.id)
                .then(() => {
                  $this.showSuccessNotification($this.$t('globalMessages.deleteDataSuccess', { system: psp.name }));
                  $this.refresh();
                })
                .catch(err => {
                  $this.showErrorNotification($this.$t('globalMessages.deleteDataError', { system: psp.name }), $this.getErrorDescription(err));
                });
          });
    },

    async refresh() {
      let $this = this;
      let loadingToken = this.setLoading();

      const result = await this.getAllPsps(
          {
            pagination: this.pagination,
            sort: { field: 'sortOrder', direction: 'ASC' },
            filter: this.filters,
          }
      )
          .catch(err => {
            $this.showErrorNotification($this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.psps') }), $this.getErrorDescription(err));
            $this.setLoaded(loadingToken);
          });
      if (result) {
        // Same order as the new-transaction dropdown: sortOrder first
        // (unset falls to the bottom), then alphabetical.
        this.psps = (result.data || []).slice().sort((a, b) => {
          const ao = a.sortOrder != null ? a.sortOrder : Number.MAX_SAFE_INTEGER;
          const bo = b.sortOrder != null ? b.sortOrder : Number.MAX_SAFE_INTEGER;
          return (ao - bo)
              || String(a.name || '').toLowerCase().localeCompare(String(b.name || '').toLowerCase());
        });
      }
      this.setLoaded(loadingToken);
    }
  }
};
</script>

<style scoped>
.btn-container .btn {
  margin-right: 2px;
}

.psp-list__row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 0.75rem;
  border-bottom: 1px solid rgba(110, 117, 159, 0.14);
  background: transparent;
}
.psp-list__row--head {
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.76rem;
  letter-spacing: 0.03em;
  border-bottom-width: 2px;
}
.psp-list__row:not(.psp-list__row--head):hover {
  background: rgba(0, 123, 255, 0.05);
}
.psp-list__cell--grip { width: 26px; flex-shrink: 0; }
.psp-list__cell--pos { width: 34px; flex-shrink: 0; }
.psp-list__cell--name { flex: 1; min-width: 140px; }
.psp-list__cell--active { width: 80px; flex-shrink: 0; }
.psp-list__cell--time { width: 170px; flex-shrink: 0; white-space: nowrap; }
.psp-list__cell--actions { width: 110px; flex-shrink: 0; }

.psp-list__grip {
  cursor: grab;
  color: #b9b9c3;
}
.psp-list__grip:hover { color: #007bff; }
.psp-list__grip--off { cursor: default; opacity: 0.35; }
.psp-list__grip--off:hover { color: #b9b9c3; }

/* vuedraggable ghost while dragging */
.sortable-ghost {
  opacity: 0.55;
  background: rgba(0, 123, 255, 0.08);
}
</style>
